import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ShoppingBag, ExternalLink, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const AMAZON_AFFILIATE_TAG = "beckenham-21";

// Helper function to ensure Amazon links have the affiliate tag
const ensureAffiliateTag = (url) => {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    // Remove existing tag if present and add our affiliate tag
    urlObj.searchParams.delete('tag');
    urlObj.searchParams.set('tag', AMAZON_AFFILIATE_TAG);
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, try simple string append
    if (url.includes('?')) {
      const cleanUrl = url.replace(/[?&]tag=[^&]*/g, '').replace(/\?&/, '?').replace(/&&/, '&');
      return cleanUrl + (cleanUrl.includes('?') ? '&' : '?') + `tag=${AMAZON_AFFILIATE_TAG}`;
    }
    return url + `?tag=${AMAZON_AFFILIATE_TAG}`;
  }
};

const ProductCard = ({ product }) => (
  <a
    href={ensureAffiliateTag(product.amazon_url)}
    target="_blank"
    rel="noopener noreferrer"
    className="group bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg hover:border-[#FF9900] transition-all duration-300"
    data-testid={`product-${product.id}`}
  >
    {/* Clean product card */}
    <div className="p-5">
      {/* Price and Category header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded">
          {product.category}
        </span>
        <span className="text-lg font-bold text-[#7C2C3B]">
          {product.price}
        </span>
      </div>
      
      {/* Product name */}
      <h3 className="font-bold text-[#0F172A] mb-4 line-clamp-2 text-base leading-tight group-hover:text-[#7C2C3B] transition-colors" style={{ fontFamily: 'Barlow Condensed' }}>
        {product.name.toUpperCase()}
      </h3>
      
      {/* Amazon CTA */}
      <div className="flex items-center justify-center gap-2 bg-[#FFD814] hover:bg-[#F7CA00] text-[#0F1111] py-2.5 px-4 rounded-full font-medium text-sm transition-colors group-hover:shadow-md">
        <span>View & Buy on Amazon</span>
        <ExternalLink className="w-4 h-4" />
      </div>
    </div>
  </a>
);

const Pagination = ({ page, totalPages, onPageChange }) => {
  const pages = [];
  const maxVisible = 5;
  
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
        className="flex items-center gap-1"
      >
        <ChevronLeft className="w-4 h-4" />
        Prev
      </Button>
      
      {start > 1 && (
        <>
          <Button variant="outline" size="sm" onClick={() => onPageChange(1)}>1</Button>
          {start > 2 && <span className="text-slate-400">...</span>}
        </>
      )}
      
      {pages.map(p => (
        <Button
          key={p}
          variant={p === page ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(p)}
          className={p === page ? "bg-[#7C2C3B] hover:bg-[#63232F]" : ""}
        >
          {p}
        </Button>
      ))}
      
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-slate-400">...</span>}
          <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)}>{totalPages}</Button>
        </>
      )}
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages}
        className="flex items-center gap-1"
      >
        Next
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [page, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/shop/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      
      const response = await axios.get(`${API}/shop/products?${params.toString()}`);
      setProducts(response.data.products);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shop-page">
      <SEO title="Shop" path="/shop" description="Official West Ham United merchandise, kits, scarves, books and gifts. Support the Hammers with authentic fan gear." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-2">
            <ShoppingBag className="w-10 h-10 text-[#F8D742]" />
            <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              ALL WHUFC SHOP
            </h1>
          </div>
          <p className="text-white/80">West Ham United merch on Amazon</p>
          <p className="text-white/60 text-sm mt-2">
            Click any product to view and purchase on Amazon.co.uk
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-slate-400" />
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-48" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-slate-500">
              Showing {products.length} of {total} products
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No products found</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {totalPages > 1 && (
              <Pagination 
                page={page} 
                totalPages={totalPages} 
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
        
        {/* Affiliate Disclaimer */}
        <div className="mt-12 p-4 bg-slate-100 rounded-lg text-center text-sm text-slate-500">
          <p>
            As an Amazon Associate, we earn from qualifying purchases. 
            Clicking on product links will take you to Amazon.co.uk where you can complete your purchase.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
